import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon, BellIcon, BookOpenIcon, ChevronRightIcon, Cog6ToothIcon, CubeIcon, DocumentTextIcon, FolderIcon, PowerIcon, QuestionMarkCircleIcon, ShieldCheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import pkg from '../../package.json';
import { logoutUser } from '../slices/authSlice';
import { markVersionSeen, selectSeenVersion, selectTheme, setTheme } from '../slices/preferencesSlice';
import BottomSheet from './ui/BottomSheet';
import PillGroup from './ui/PillGroup';
import ConfirmDialog from './ConfirmDialog';

const MAIN = [
  { to: '/data/resource', label: 'Resources', sub: 'Targets, rewards and API details', Icon: CubeIcon },
  { to: '/files', label: 'Files', sub: 'Exports and uploads, easier on a larger screen', Icon: FolderIcon },
  { to: '/settings', label: 'Settings', sub: 'Profile, password and notifications', Icon: Cog6ToothIcon },
  { to: '/notices', label: 'Notices', sub: 'Maintenance, clock changes and past messages', Icon: BellIcon },
];
const THEME_OPTIONS = [{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }, { value: 'system', label: 'System' }];
const row = 'flex min-h-[56px] w-full items-center gap-3 rounded-xl px-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500';
const icon = 'h-[22px] w-[22px] flex-none text-gray-500 dark:text-gray-400';
const label = 'text-[15px] font-medium text-gray-900 dark:text-gray-100';

// Forecaster phones: the destinations the tab bar does not carry.
export default function MobileMoreSheet({ open, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector(selectTheme);
  const seenVersion = useSelector(selectSeenVersion);
  const username = useSelector((s) => s.user?.userDetails?.username);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const hasNewRelease = !!pkg?.version && seenVersion !== pkg.version;
  const go = (to) => { onClose(); navigate(to); };

  return (
    <>
      <BottomSheet open={open} onClose={onClose} title="More">
        <nav aria-label="More destinations" className="flex flex-col">
          {MAIN.map(({ to, label: text, sub, Icon }) => (
            <button key={to} type="button" onClick={() => go(to)} className={row}>
              <Icon className={icon} aria-hidden="true" />
              <span className="flex min-w-0 flex-1 flex-col"><span className={label}>{text}</span><span className="text-xs text-gray-500 dark:text-gray-400">{sub}</span></span>
              <ChevronRightIcon className="h-4 w-4 flex-none text-gray-400" aria-hidden="true" />
            </button>
          ))}
          <div className="flex min-h-[56px] flex-wrap items-center justify-between gap-x-3 gap-y-2 px-2">
            <span className={label}>Theme</span>
            <PillGroup options={THEME_OPTIONS} value={theme} onChange={(v) => dispatch(setTheme(v))} ariaLabel="Theme" />
          </div>
          <p className="px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Help</p>
          <a href="/docs/" target="_blank" rel="noopener noreferrer" onClick={onClose} className={row}>
            <BookOpenIcon className={icon} aria-hidden="true" /><span className={`flex-1 ${label}`}>Documentation</span>
            <ArrowTopRightOnSquareIcon className="h-4 w-4 flex-none text-gray-400" aria-hidden="true" />
          </a>
          <button type="button" onClick={() => { if (hasNewRelease) dispatch(markVersionSeen(pkg.version)); go('/changelog'); }} className={row}>
            <SparklesIcon className={icon} aria-hidden="true" />
            <span className="flex flex-1 items-center gap-2">
              <span className={label}>Changelog</span>
              {hasNewRelease && <span className="rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700 dark:border-brand-800 dark:bg-brand-900/30 dark:text-brand-300">New · v{pkg.version}</span>}
            </span>
            <ChevronRightIcon className="h-4 w-4 flex-none text-gray-400" aria-hidden="true" />
          </button>
          <button type="button" onClick={() => go('/faq')} className={row}>
            <QuestionMarkCircleIcon className={icon} aria-hidden="true" /><span className={`flex-1 ${label}`}>FAQ</span>
            <ChevronRightIcon className="h-4 w-4 flex-none text-gray-400" aria-hidden="true" />
          </button>
          <a href="/terms" target="_blank" rel="noopener noreferrer" onClick={onClose} className={row}>
            <DocumentTextIcon className={icon} aria-hidden="true" /><span className={`flex-1 ${label}`}>Terms of Use</span>
            <ArrowTopRightOnSquareIcon className="h-4 w-4 flex-none text-gray-400" aria-hidden="true" />
          </a>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={onClose} className={row}>
            <ShieldCheckIcon className={icon} aria-hidden="true" /><span className={`flex-1 ${label}`}>Privacy Policy</span>
            <ArrowTopRightOnSquareIcon className="h-4 w-4 flex-none text-gray-400" aria-hidden="true" />
          </a>
          <div className="mx-2 my-2 h-px bg-gray-200/70 dark:bg-gray-600/70" />
          <button type="button" onClick={() => setConfirmOpen(true)} className={row}>
            <PowerIcon className={icon} aria-hidden="true" /><span className={label}>Sign out</span>
          </button>
        </nav>
        <div className="flex justify-between px-2 pt-2 text-xs text-gray-500 dark:text-gray-400">
          <span>{username ? `Signed in as ${username}` : ''}</span>
          <span className="font-mono">v{pkg.version}</span>
        </div>
      </BottomSheet>
      <ConfirmDialog
        open={confirmOpen}
        title="Sign Out"
        description="Are you sure you want to sign out?"
        confirmText="Sign Out"
        cancelText="Cancel"
        onConfirm={() => { dispatch(logoutUser()); setConfirmOpen(false); navigate('/', { replace: true }); }}
        onCancel={() => setConfirmOpen(false)}
        iconBg="bg-red-100"
        iconColor="text-red-600"
      />
    </>
  );
}
